import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Snackbar,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  useTheme as useMuiTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import LayersIcon from "@mui/icons-material/Layers";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import DescriptionIcon from "@mui/icons-material/Description";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import WorkIcon from "@mui/icons-material/Work";
import CloseIcon from "@mui/icons-material/Close";
import RepeatIcon from "@mui/icons-material/Repeat";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../contexts/AuthContext";
import { hasLeaveRole } from "../../services/api";
import reservationApi from "../../services/reservation-api";
import {
  ReservableAsset,
  Reservation,
  RecurrencePattern,
} from "../../types/reservation";
import AssetDialog from "./AssetDialog";
import ReservationDialog from "./ReservationDialog";
import DeleteConfirmDialog from "./DeleteConfirmDialog";
import DeleteReservationDialog from "./DeleteReservationDialog";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import TodayIcon from "@mui/icons-material/Today";

// Helper function to parse date from backend UTC format to local date string
// Backend returns date as array [year, month, day] or string "YYYY-MM-DD" in UTC
// We need to convert to local timezone for display
const parseDateToLocalString = (date: any, time?: any): string => {
  if (!date) return "";
  if (Array.isArray(date)) {
    return `${date[0]}-${String(date[1]).padStart(2, "0")}-${String(
      date[2],
    ).padStart(2, "0")}`;
  }
  return date;
};

// Helper function to parse time from backend UTC format to local time string
// Backend returns time as array [hours, minutes] or string "HH:mm" in UTC
// We need to convert to local timezone for display
const parseTimeToLocalString = (date: any, time: any): string => {
  if (!time) return "";
  if (Array.isArray(time)) {
    return `${String(time[0]).padStart(2, "0")}:${String(time[1]).padStart(
      2,
      "0",
    )}`;
  }
  return time;
};

// Helper function to parse UTC time to local minutes for timeline calculations
const parseTimeToLocalMinutes = (date: any, time: any): number => {
  const localTime = parseTimeToLocalString(date, time);
  if (!localTime) return 0;

  const [hours, minutes] = localTime.split(":").map(Number);
  return hours * 60 + minutes;
};

// Timeline Bar Component - 30-minute slots, scrollable, auto-scroll to current time
const SLOT_WIDTH = 40; // Width of each 30-minute slot in pixels
const TOTAL_SLOTS = 48; // 24 hours * 2 slots per hour
const TIMELINE_WIDTH = SLOT_WIDTH * TOTAL_SLOTS; // Total width: 1920px

interface TimelineBarProps {
  reservations: Reservation[];
  date: string;
  currentUserId?: number;
  isAdmin: boolean;
  onDelete: (reservation: Reservation) => void;
  isDarkMode: boolean;
}

const TimelineBar: React.FC<TimelineBarProps> = ({
  reservations,
  date,
  currentUserId,
  isAdmin,
  onDelete,
  isDarkMode,
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [currentTimePos, setCurrentTimePos] = useState(0);

  // Theme-aware colors
  const colors = {
    border: isDarkMode ? "rgba(255,255,255,0.12)" : "#e2e8f0",
    borderLight: isDarkMode ? "rgba(255,255,255,0.06)" : "#f1f5f9",
    background: isDarkMode ? "#1e1e1e" : "#f8fafc",
    text: isDarkMode ? "#e0e0e0" : "#64748b",
    scrollTrack: isDarkMode ? "rgba(255,255,255,0.05)" : "#f1f5f9",
    scrollThumb: isDarkMode ? "rgba(255,255,255,0.2)" : "#cbd5e1",
    scrollThumbHover: isDarkMode ? "rgba(255,255,255,0.3)" : "#94a3b8",
    myReservation: isDarkMode ? "#6db1e7" : "#6db1e7",
    otherReservation: isDarkMode ? "#94a3b8" : "#64748b",
  };

  // Filter reservations for the selected date (in local timezone)
  const todaysReservations = useMemo(() => {
    const selectedDateStr = date; // Format: YYYY-MM-DD (local)
    return reservations.filter((res) => {
      // Convert UTC reservation date/time to local date for comparison
      const resLocalDate = parseDateToLocalString(
        res.reservationDate,
        res.startTime,
      );
      return resLocalDate === selectedDateStr;
    });
  }, [reservations, date]);

  // Update current time indicator every minute
  useEffect(() => {
    const updateCurrentTime = () => {
      const now = new Date();
      const minutes = now.getHours() * 60 + now.getMinutes();
      setCurrentTimePos((minutes / 30) * SLOT_WIDTH);
    };

    updateCurrentTime();
    const interval = setInterval(updateCurrentTime, 60000); // Every minute

    return () => clearInterval(interval);
  }, []);

  // Auto-scroll to current time on mount
  useEffect(() => {
    if (scrollContainerRef.current) {
      // Calculate scroll position to center current time in view
      const containerWidth = scrollContainerRef.current.clientWidth;
      const scrollTo = Math.max(0, currentTimePos - containerWidth / 2);
      scrollContainerRef.current.scrollLeft = scrollTo;
    }
  }, [currentTimePos]);

  // Generate time labels for every hour
  const timeLabels = useMemo(() => {
    const labels = [];
    for (let hour = 0; hour < 24; hour++) {
      labels.push({
        hour,
        label: `${String(hour).padStart(2, "0")}:00`,
        position: hour * 2 * SLOT_WIDTH, // 2 slots per hour
      });
    }
    return labels;
  }, []);

  return (
    <Box
      sx={{
        position: "relative",
        mt: 2,
        borderRadius: 2,
        border: `1px solid ${colors.border}`,
        overflow: "hidden",
        width: "100%",
        maxWidth: "100%",
        bgcolor: colors.background,
      }}
    >
      {/* Scrollable container */}
      <Box
        ref={scrollContainerRef}
        sx={{
          overflowX: "auto",
          overflowY: "hidden",
          width: "100%",
          "&::-webkit-scrollbar": {
            height: 6,
          },
          "&::-webkit-scrollbar-track": {
            bgcolor: colors.scrollTrack,
          },
          "&::-webkit-scrollbar-thumb": {
            bgcolor: colors.scrollThumb,
            borderRadius: 3,
            "&:hover": { bgcolor: colors.scrollThumbHover },
          },
        }}
      >
        {/* Timeline content with fixed width */}
        <Box
          sx={{
            position: "relative",
            width: TIMELINE_WIDTH,
            minWidth: TIMELINE_WIDTH,
            height: 56,
          }}
        >
          {/* Time labels row */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: 16,
              display: "flex",
              borderBottom: `1px solid ${colors.border}`,
            }}
          >
            {timeLabels.map(({ hour, label, position }) => (
              <Typography
                key={hour}
                sx={{
                  position: "absolute",
                  left: position,
                  fontSize: "9px",
                  color: colors.text,
                  fontWeight: 500,
                  px: 0.5,
                }}
              >
                {label}
              </Typography>
            ))}
          </Box>

          {/* Grid lines for 30-minute slots */}
          <Box
            sx={{
              position: "absolute",
              top: 16,
              left: 0,
              width: "100%",
              height: 40,
              display: "flex",
            }}
          >
            {Array.from({ length: TOTAL_SLOTS }).map((_, i) => (
              <Box
                key={i}
                sx={{
                  width: SLOT_WIDTH,
                  height: "100%",
                  borderRight:
                    i % 2 === 1
                      ? `1px solid ${colors.border}`
                      : `1px solid ${colors.borderLight}`,
                }}
              />
            ))}
          </Box>

          {/* Current Time Indicator */}
          {date === new Date().toISOString().split("T")[0] && (
            <Box
              sx={{
                position: "absolute",
                left: currentTimePos,
                top: 16,
                bottom: 0,
                width: "2px",
                bgcolor: "red",
                zIndex: 30,
              }}
            />
          )}

          {/* Reservation bars */}
          {todaysReservations.map((res) => {
            // For continuous (multi-day) reservations, determine what to show for this day
            const isContinuous =
              !!res.continuousGroupId &&
              res.continuousStartDate &&
              res.continuousEndDate;

            let effectiveStartTime: any = res.startTime;
            let effectiveEndTime: any = res.endTime;

            if (isContinuous) {
              // Parse dates to compare
              const currentDateStr = parseDateToLocalString(
                res.reservationDate,
                res.startTime,
              );
              const contStartDateStr = parseDateToLocalString(
                res.continuousStartDate,
                res.continuousStartTime,
              );
              const contEndDateStr = parseDateToLocalString(
                res.continuousEndDate,
                res.continuousEndTime,
              );

              const isFirstDay = currentDateStr === contStartDateStr;
              const isLastDay = currentDateStr === contEndDateStr;

              if (isFirstDay && isLastDay) {
                // Single day within continuous - use actual times
                effectiveStartTime = res.continuousStartTime;
                effectiveEndTime = res.continuousEndTime;
              } else if (isFirstDay) {
                // First day: start at continuousStartTime, end at 23:59
                effectiveStartTime = res.continuousStartTime;
                effectiveEndTime = [23, 59];
              } else if (isLastDay) {
                // Last day: start at 00:00, end at continuousEndTime
                effectiveStartTime = [0, 0];
                effectiveEndTime = res.continuousEndTime;
              } else {
                // Middle day: full day 00:00 - 23:59
                effectiveStartTime = [0, 0];
                effectiveEndTime = [23, 59];
              }
            }

            let startMinutes = effectiveStartTime
              ? parseTimeToLocalMinutes(res.reservationDate, effectiveStartTime)
              : 0;
            let endMinutes = effectiveEndTime
              ? parseTimeToLocalMinutes(res.reservationDate, effectiveEndTime)
              : 1440;

            if (endMinutes <= startMinutes) {
              endMinutes = startMinutes + 60;
            }

            // Format time strings for display
            const startTimeStr = parseTimeToLocalString(
              res.reservationDate,
              effectiveStartTime,
            );
            const endTimeStr = parseTimeToLocalString(
              res.reservationDate,
              effectiveEndTime,
            );

            const duration = endMinutes - startMinutes;
            // Calculate position in pixels based on 30-minute slots
            const leftPx = (startMinutes / 30) * SLOT_WIDTH;
            const widthPx = Math.max(
              (duration / 30) * SLOT_WIDTH,
              SLOT_WIDTH / 2,
            ); // Min half slot width

            console.log(res.employeeId, currentUserId);
            const isMyRes = res.employeeId === currentUserId;
            const canModify = isMyRes || isAdmin;
            const displayName = res.employeeName?.split(" ")[0] || "Reserved";

            return (
              <Box
                key={res.id}
                sx={{
                  position: "absolute",
                  height: 36,
                  top: 18,
                  borderRadius: "6px",
                  transition: "all 0.2s",
                  cursor: "pointer",
                  "&:hover": {
                    filter: "brightness(1.1)",
                    zIndex: 20,
                    boxShadow: isDarkMode
                      ? "0 4px 12px rgba(0,0,0,0.4)"
                      : "0 4px 12px rgba(0,0,0,0.15)",
                  },
                  "&:hover .delete-btn": { opacity: 1 },
                  zIndex: 10,
                  bgcolor: isMyRes
                    ? colors.myReservation
                    : colors.otherReservation,
                  left: leftPx,
                  width: widthPx,
                  boxShadow: isDarkMode
                    ? "0 1px 3px rgba(0,0,0,0.3)"
                    : "0 1px 3px rgba(0,0,0,0.1)",
                }}
                title={`${startTimeStr || "00:00"} - ${endTimeStr || "23:59"}\n${isMyRes ? "Your reservation" : `Reserved by ${res.employeeName || "Unknown"}`}`}
              >
                {canModify && (
                  <IconButton
                    className="delete-btn"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(res);
                    }}
                    sx={{
                      position: "absolute",
                      top: 2,
                      right: 2,
                      p: 0.25,
                      bgcolor: "rgba(0,0,0,0.3)",
                      "&:hover": { bgcolor: "#ef4444" },
                      color: "white",
                      opacity: 0,
                      transition: "opacity 0.2s",
                      width: 16,
                      height: 16,
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 10 }} />
                  </IconButton>
                )}
                <Box
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                    px: 0.5,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "10px",
                      color: "white",
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      lineHeight: 1.2,
                    }}
                  >
                    {displayName}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

const ReservationsPage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const muiTheme = useMuiTheme();
  const isDarkMode = muiTheme.palette.mode === "dark";

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [assets, setAssets] = useState<ReservableAsset[]>([]);
  const [reservations, setReservations] = useState<Reservation[]>([]);

  // Theme-aware colors for the main component
  const themeColors = {
    background: isDarkMode ? "#121212" : "#f5f8ff",
    cardBg: isDarkMode ? "#1e1e1e" : "#ffffff",
    border: isDarkMode ? "rgba(255,255,255,0.12)" : "#e2e8f0",
    borderLight: isDarkMode ? "rgba(255,255,255,0.06)" : "#f1f5f9",
    textPrimary: isDarkMode ? "#e0e0e0" : "#1e293b",
    textSecondary: isDarkMode ? "rgba(224,224,224,0.7)" : "#64748b",
    textMuted: isDarkMode ? "rgba(224,224,224,0.5)" : "#94a3b8",
    inputBg: isDarkMode ? "rgba(255,255,255,0.05)" : "#f8fafc",
    tagBg: isDarkMode ? "rgba(255,255,255,0.08)" : "#f1f5f9",
    tagBgCategory: isDarkMode ? "rgba(217,119,6,0.15)" : "#fffbeb",
    tagTextCategory: isDarkMode ? "#fbbf24" : "#d97706",
    myBookingBg: isDarkMode
      ? "rgb(109, 177, 231, 0.15)"
      : "rgb(109, 177, 231, 0.15)",
    myBookingBorder: isDarkMode
      ? "rgb(109, 177, 231, 0.15)"
      : "rgb(109, 177, 231, 0.15)",
    myBookingText: isDarkMode ? "#6db1e7" : "#2a4d73",
    otherBookingBg: isDarkMode ? "rgba(255,255,255,0.05)" : "#f8fafc",
    hoverBg: isDarkMode ? "rgba(255,255,255,0.08)" : "#f1f5f9",
    hoverBgDanger: isDarkMode ? "rgba(239,68,68,0.15)" : "#fef2f2",
    primary: isDarkMode ? "#6db1e7" : "#6db1e7",
    primaryHover: isDarkMode ? "#6366f1" : "#4338ca",
    emptyStateBg: isDarkMode ? "rgba(255,255,255,0.05)" : "#f1f5f9",
    emptyStateBorder: isDarkMode ? "rgba(255,255,255,0.12)" : "#cbd5e1",
    legendBg: isDarkMode ? "rgba(255,255,255,0.03)" : "rgba(248,250,252,0.5)",
    addButtonBg: isDarkMode ? "#374151" : "#0f172a",
    addButtonHover: isDarkMode ? "#4b5563" : "#1e293b",
  };

  // Filter states
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split("T")[0],
  ); // Today
  const [selectedType, setSelectedType] = useState<string>("ALL");
  const [selectedCategory, setSelectedCategory] = useState<string>("ALL");

  // Dialog states
  const [assetDialogOpen, setAssetDialogOpen] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState<ReservableAsset | null>(
    null,
  );
  const [reservationDialogOpen, setReservationDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [assetToDelete, setAssetToDelete] = useState<ReservableAsset | null>(
    null,
  );
  const [reservationDeleteDialogOpen, setReservationDeleteDialogOpen] =
    useState(false);
  const [reservationToDelete, setReservationToDelete] =
    useState<Reservation | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Check if user has ADMIN role using the standard hasLeaveRole function
  const isAdmin = hasLeaveRole(user ? user : null, "ADMIN");
  const currentUserId = user?.id;

// Date navigation helpers
  const getTodayString = () => new Date().toISOString().split("T")[0];

  const isToday = selectedDate === getTodayString();

  const navigateToToday = () => {
    setSelectedDate(getTodayString());
  };

  const navigateToPreviousDay = () => {
    const date = new Date(selectedDate);
    date.setDate(date.getDate() - 1);
    setSelectedDate(date.toISOString().split("T")[0]);
  };

  const navigateToNextDay = () => {
    const date = new Date(selectedDate);
    date.setDate(date.getDate() + 1);
    setSelectedDate(date.toISOString().split("T")[0]);
  };

  useEffect(() => {
    loadData();
  }, []);

  // Reload reservations when selected date changes
  useEffect(() => {
    loadReservations();
  }, [selectedDate]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const assetsData = await reservationApi.getActiveAssets();
      setAssets(assetsData);
      await loadReservations();
    } catch (err: any) {
      setError(err.message || "Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const loadReservations = async () => {
    try {
      // Fetch reservations for selected date (using date range for the same day)
      const reservationsData = await reservationApi.getReservationsByDateRange(
        selectedDate,
        selectedDate,
        Intl.DateTimeFormat().resolvedOptions().timeZone,
      );
      setReservations(reservationsData);
    } catch (err: any) {
      console.error("Failed to load reservations:", err);
    }
  };

  // Get unique types and categories from assets (dynamic from existing assets)
  const availableTypes = Array.from(
    new Set(assets.map((a) => a.type).filter(Boolean)),
  );
  const availableCategories = Array.from(
    new Set(assets.map((a) => a.category).filter(Boolean)),
  );

  // Filter assets based on search and filters
  const filteredAssets = assets.filter((asset) => {
    // Search filter (fuzzy search across name, type, category, description)
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesSearch =
        asset.name.toLowerCase().includes(query) ||
        asset.type.toLowerCase().includes(query) ||
        (asset.category && asset.category.toLowerCase().includes(query)) ||
        (asset.description && asset.description.toLowerCase().includes(query));
      if (!matchesSearch) return false;
    }

    // Type filter
    if (selectedType !== "ALL" && asset.type !== selectedType) {
      return false;
    }

    // Category filter
    if (selectedCategory !== "ALL" && asset.category !== selectedCategory) {
      return false;
    }

    return true;
  });

  // Asset Management Handlers
  const handleAddAsset = () => {
    setSelectedAsset(null);
    setAssetDialogOpen(true);
  };

  const handleEditAsset = (asset: ReservableAsset) => {
    setSelectedAsset(asset);
    setAssetDialogOpen(true);
  };

  const handleDeleteAsset = (asset: ReservableAsset) => {
    setAssetToDelete(asset);
    setDeleteDialogOpen(true);
  };

  const handleSaveAsset = async (assetData: Partial<ReservableAsset>) => {
    try {
      if (selectedAsset) {
        // Update existing asset
        await reservationApi.updateAsset(selectedAsset.id!, assetData);
        setSuccessMessage("Asset updated successfully");
      } else {
        // Create new asset
        await reservationApi.createAsset(assetData);
        setSuccessMessage("Asset created successfully");
      }
      await loadData();
      setAssetDialogOpen(false);
    } catch (err: any) {
      setError(err.message || "Failed to save asset");
      throw err;
    }
  };

  const handleConfirmDelete = async () => {
    if (!assetToDelete) return;
    try {
      await reservationApi.deleteAsset(assetToDelete.id!);
      setSuccessMessage("Asset deleted successfully");
      await loadData();
      setDeleteDialogOpen(false);
      setAssetToDelete(null);
    } catch (err: any) {
      setError(err.message || "Failed to delete asset");
      throw err;
    }
  };

  // Reservation Handlers
  const handleReserveAsset = (asset: ReservableAsset) => {
    setSelectedAsset(asset);
    setReservationDialogOpen(true);
  };

  const handleCreateReservation = async (data: {
    startDate: string;
    endDate?: string;
    startTime?: string;
    endTime?: string;
    purpose?: string;
    isAllDay?: boolean;
    reservationType?: string;
    recurrencePattern?: string;
    customDays?: boolean[];
  }) => {
    if (!selectedAsset) return;
    try {
      // Handle recurring reservations
      if (data.reservationType === "recurring" && data.recurrencePattern) {
        // Map custom days to DayOfWeek format for backend
        let daysOfWeek: string[] | undefined;
        if (data.recurrencePattern === "custom" && data.customDays) {
          const dayNames = [
            "SUNDAY",
            "MONDAY",
            "TUESDAY",
            "WEDNESDAY",
            "THURSDAY",
            "FRIDAY",
            "SATURDAY",
          ];
          daysOfWeek = data.customDays
            .map((selected, idx) => (selected ? dayNames[idx] : null))
            .filter((day): day is string => day !== null);
        } else if (data.recurrencePattern === "weekly") {
          // Default to weekdays for weekly pattern
          daysOfWeek = ["MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY"];
        }

        await reservationApi.createRecurringReservation({
          assetId: selectedAsset.id!,
          startDate: data.startDate,
          endDate: data.endDate || data.startDate,
          startTime: data.startTime,
          endTime: data.endTime,
          isAllDay: data.isAllDay ?? false,
          purpose: data.purpose,
          pattern:
            data.recurrencePattern === "daily"
              ? RecurrencePattern.DAILY
              : RecurrencePattern.WEEKLY,
          daysOfWeek,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        });
      } else {
        // Handle single or continuous (multi-day) reservations
        await reservationApi.createReservation({
          assetId: selectedAsset.id!,
          startDate: data.startDate,
          endDate: data.endDate,
          startTime: data.startTime,
          endTime: data.endTime,
          purpose: data.purpose,
          isAllDay: data.isAllDay ?? false,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        });
      }
      setSuccessMessage("Reservation created successfully");
      await loadData();
      setReservationDialogOpen(false);
    } catch (err: any) {
      setError(err.message || "Failed to create reservation");
      throw err;
    }
  };

  // Open the delete reservation dialog
  const handleDeleteReservationClick = (reservation: Reservation) => {
    setReservationToDelete(reservation);
    setReservationDeleteDialogOpen(true);
  };

  // Handle deleting a single reservation
  const handleDeleteSingleReservation = async (reservationId: number) => {
    try {
      await reservationApi.cancelReservation(reservationId);
      setSuccessMessage("Reservation cancelled successfully");
      await loadData();
    } catch (err: any) {
      setError(err.message || "Failed to cancel reservation");
      throw err;
    }
  };

  // Handle deleting all recurring reservations
  const handleDeleteRecurringReservations = async (groupId: string) => {
    try {
      await reservationApi.cancelRecurringReservations(groupId);
      setSuccessMessage("All recurring reservations cancelled successfully");
      await loadData();
    } catch (err: any) {
      setError(err.message || "Failed to cancel recurring reservations");
      throw err;
    }
  };

  // Handle deleting all continuous (multi-day) reservations
  const handleDeleteContinuousReservations = async (groupId: string) => {
    try {
      await reservationApi.cancelContinuousReservations(groupId);
      setSuccessMessage("Multi-day reservation cancelled successfully");
      await loadData();
    } catch (err: any) {
      setError(err.message || "Failed to cancel multi-day reservation");
      throw err;
    }
  };

  return (
    <Box sx={{ minHeight: "100vh" }}>
      <Container maxWidth="lg" sx={{ py: 2 }}>
        {/* Controls Bar - Date and Legend Row */}
        <Box sx={{ mb: 2 }}>
          {/* Date & Legend Row */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { xs: "flex-start", sm: "center" },
              justifyContent: "space-between",
              gap: 2,
              bgcolor: themeColors.cardBg,
              p: 2,
              borderRadius: 4,
              boxShadow: isDarkMode
                ? "0 1px 3px rgba(0,0,0,0.2)"
                : "0 1px 3px rgba(0,0,0,0.05)",
              border: `1px solid ${themeColors.border}`,
              mb: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                width: { xs: "100%", sm: "auto" },
              }}
            >
              {/* Previous Day Button */}
              <IconButton
                onClick={navigateToPreviousDay}
                size="small"
                sx={{
                  color: themeColors.textSecondary,
                  bgcolor: themeColors.inputBg,
                  "&:hover": {
                    bgcolor: themeColors.tagBg,
                  },
                }}
                title={t('reservations.previousDay')}
              >
                <ChevronLeftIcon fontSize="small" />
              </IconButton>
              <Box
                sx={{
                  p: 1.25,
                  borderRadius: 2,
                  color: themeColors.textSecondary,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CalendarTodayIcon sx={{ fontSize: 20 }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography
                  sx={{
                    fontSize: "10px",
                    fontWeight: 700,
                    color: themeColors.textMuted,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    mb: 0.5,
                  }}
                >
                  {t("reservations.viewScheduleFor")}
                </Typography>
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  style={{
                    width: "100%",
                    background: "transparent",
                    border: "none",
                    fontWeight: 600,
                    color: themeColors.textPrimary,
                    fontSize: "0.875rem",
                    cursor: "pointer",
                    outline: "none",
                    colorScheme: isDarkMode ? "dark" : "light",
                  }}
                />
              </Box>
              {/* Next Day Button */}
              <IconButton
                onClick={navigateToNextDay}
                size="small"
                sx={{
                  color: themeColors.textSecondary,
                  bgcolor: themeColors.inputBg,
                  "&:hover": {
                    bgcolor: themeColors.tagBg,
                  },
                }}
                title={t('reservations.nextDay')}
              >
                <ChevronRightIcon fontSize="small" />
              </IconButton>

              {/* Today Button - only show if not already on today */}
              {!isToday && (
                <Button
                  onClick={navigateToToday}
                  size="small"
                  startIcon={<TodayIcon />}
                  sx={{
                    color: themeColors.textPrimary,
                    bgcolor: themeColors.inputBg,
                    textTransform: "none",
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    px: 1.5,
                    py: 0.5,
                    "&:hover": {
                      bgcolor: themeColors.tagBg,
                    },
                  }}
                >
                  {t('reservations.gotoToday')}
                </Button>
              )}

            </Box>

            {/* Legend */}
            <Box
              sx={{
                display: "flex",
                gap: 3,
                fontSize: "0.75rem",
                fontWeight: 500,
                color: themeColors.textSecondary,
                bgcolor: themeColors.legendBg,
                px: 2,
                py: 1,
                borderRadius: 2,
                border: `1px solid ${themeColors.borderLight}`,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: 0.5,
                    bgcolor: isDarkMode ? "#6db1e7" : "#6db1e7",
                    boxShadow: isDarkMode
                      ? "0 1px 2px rgba(0,0,0,0.3)"
                      : "0 1px 2px rgba(0,0,0,0.1)",
                  }}
                />
                <span>{t("reservations.myReservation")}</span>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: 0.5,
                    bgcolor: isDarkMode ? "#94a3b8" : "#64748b",
                    boxShadow: isDarkMode
                      ? "0 1px 2px rgba(0,0,0,0.3)"
                      : "0 1px 2px rgba(0,0,0,0.1)",
                  }}
                />
                <span>
                  {}
                  {t("reservations.otherReservation")}
                </span>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Filter Row */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 2,
            mb: 2,
            alignItems: "stretch",
          }}
        >
          {/* Search and Filters Container */}
          <Box
            sx={{
              flex: 1,
              bgcolor: themeColors.cardBg,
              p: 2,
              borderRadius: 3,
              border: `1px solid ${themeColors.border}`,
              boxShadow: isDarkMode
                ? "0 1px 3px rgba(0,0,0,0.2)"
                : "0 1px 3px rgba(0,0,0,0.05)",
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            {/* Search Input */}
            <SearchIcon
              sx={{ color: themeColors.textMuted, fontSize: 18, flexShrink: 0 }}
            />
            <input
              type="text"
              placeholder={t("common.search")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                flex: 1,
                minWidth: 120,
                padding: "8px 0",
                background: "transparent",
                border: "none",
                fontSize: "0.875rem",
                outline: "none",
                color: themeColors.textPrimary,
              }}
            />

            {/* Type Filter */}
            <FormControl size="small" sx={{ minWidth: 120, flexShrink: 0 }}>
              <Select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                displayEmpty
                startAdornment={
                  <FilterListIcon
                    sx={{
                      color: themeColors.textMuted,
                      fontSize: 14,
                      mr: 0.5,
                    }}
                  />
                }
                sx={{
                  bgcolor: themeColors.inputBg,
                  borderRadius: 2,
                  fontSize: "0.875rem",
                  color: themeColors.textSecondary,
                  "& .MuiOutlinedInput-notchedOutline": {
                    border: "1px solid transparent",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: themeColors.border,
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: themeColors.border,
                  },
                  "& .MuiSelect-select": {
                    py: 0.75,
                    pl: 0.5,
                  },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      bgcolor: themeColors.cardBg,
                      border: `1px solid ${themeColors.border}`,
                      "& .MuiMenuItem-root": {
                        fontSize: "0.875rem",
                        color: themeColors.textSecondary,
                        "&:hover": {
                          bgcolor: themeColors.hoverBg,
                        },
                        "&.Mui-selected": {
                          bgcolor: themeColors.hoverBg,
                          "&:hover": {
                            bgcolor: themeColors.hoverBg,
                          },
                        },
                      },
                    },
                  },
                }}
              >
                <MenuItem value="ALL">
                  {}
                  {t("reservations.allTypes")}
                </MenuItem>
                {availableTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Category Filter */}
            <Box
              sx={{
                position: "relative",
                display: "flex",
                alignItems: "center",
                flexShrink: 0,
              }}
            >
              <LayersIcon
                sx={{
                  position: "absolute",
                  left: 8,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: themeColors.textMuted,
                  fontSize: 14,
                  pointerEvents: "none",
                  zIndex: 1,
                }}
              />
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                style={{
                  paddingLeft: 28,
                  paddingRight: 24,
                  paddingTop: 6,
                  paddingBottom: 6,
                  background: themeColors.inputBg,
                  border: "1px solid transparent",
                  borderRadius: 8,
                  fontSize: "0.875rem",
                  color: themeColors.textSecondary,
                  cursor: "pointer",
                  outline: "none",
                  transition: "border-color 0.2s",
                  WebkitAppearance: "none",
                  MozAppearance: "none",
                  appearance: "none",
                  colorScheme: isDarkMode ? "dark" : "light",
                  backgroundPosition: "right 8px center",
                }}
                onMouseOver={(e) =>
                  (e.currentTarget.style.borderColor = themeColors.border)
                }
                onMouseOut={(e) =>
                  (e.currentTarget.style.borderColor = "transparent")
                }
              >
                <option value="ALL">
                  {}
                  {t("reservations.allCategories")}
                </option>
                {availableCategories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
            </Box>
          </Box>

          {/* Add Asset Button (Admin only) */}
          {isAdmin && (
            <Button
              variant="contained"
              startIcon={<AddIcon sx={{ fontSize: 16 }} />}
              onClick={handleAddAsset}
              sx={{
                bgcolor: themeColors.addButtonBg,
                "&:hover": { bgcolor: themeColors.addButtonHover },
                "&:active": { transform: "scale(0.95)" },
                borderRadius: 3,
                px: 2.5,
                py: 1.5,
                fontWeight: 500,
                fontSize: "0.875rem",
                boxShadow: isDarkMode
                  ? "0 10px 15px -3px rgba(0,0,0,0.3), 0 4px 6px -2px rgba(0,0,0,0.2)"
                  : "0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)",
                whiteSpace: "nowrap",
                textTransform: "none",
                transition: "all 0.2s",
                alignSelf: "stretch",
                minHeight: 48,
              }}
            >
              {t("reservations.addAsset")}
            </Button>
          )}
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        {/* Loading State */}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          /* Assets Grid - 2 Column Layout */
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              gap: 3,
            }}
          >
            {filteredAssets.length === 0 ? (
              <Box
                sx={{
                  gridColumn: "1 / -1",
                  py: 8,
                  textAlign: "center",
                  bgcolor: themeColors.emptyStateBg,
                  borderRadius: 4,
                  border: `2px dashed ${themeColors.emptyStateBorder}`,
                }}
              >
                <WorkIcon
                  sx={{
                    fontSize: 48,
                    color: themeColors.textMuted,
                    opacity: 0.3,
                    mb: 2,
                  }}
                />
                <Typography color="text.secondary">
                  No reservable assets found.
                </Typography>
                {isAdmin && (
                  <Typography
                    variant="body2"
                    sx={{ mt: 1, color: themeColors.primary }}
                  >
                    Use "Add Asset" to create machines.
                  </Typography>
                )}
              </Box>
            ) : (
              filteredAssets.map((asset) => {
                const assetReservations = reservations.filter(
                  (r) => r.assetId === asset.id,
                );

                const now = new Date();
                const isCurrentlyInUse = assetReservations.some((res) => {
                  const resLocalDate = parseDateToLocalString(
                    res.reservationDate,
                    res.startTime,
                  );
                  if (resLocalDate !== new Date().toISOString().split("T")[0]) {
                    return false; // Not today
                  }

                  // For continuous (multi-day) reservations, determine effective times for today
                  const isContinuous =
                    !!res.continuousGroupId &&
                    res.continuousStartDate &&
                    res.continuousEndDate;

                  let effectiveStartTime: any = res.startTime;
                  let effectiveEndTime: any = res.endTime;

                  if (isContinuous) {
                    // Parse dates to compare
                    const currentDateStr = parseDateToLocalString(
                      res.reservationDate,
                      res.startTime,
                    );
                    const contStartDateStr = parseDateToLocalString(
                      res.continuousStartDate,
                      res.continuousStartTime,
                    );
                    const contEndDateStr = parseDateToLocalString(
                      res.continuousEndDate,
                      res.continuousEndTime,
                    );

                    const isFirstDay = currentDateStr === contStartDateStr;
                    const isLastDay = currentDateStr === contEndDateStr;

                    if (isFirstDay && isLastDay) {
                      // Single day within continuous - use actual times
                      effectiveStartTime = res.continuousStartTime;
                      effectiveEndTime = res.continuousEndTime;
                    } else if (isFirstDay) {
                      // First day: start at continuousStartTime, end at 23:59
                      effectiveStartTime = res.continuousStartTime;
                      effectiveEndTime = [23, 59];
                    } else if (isLastDay) {
                      // Last day: start at 00:00, end at continuousEndTime
                      effectiveStartTime = [0, 0];
                      effectiveEndTime = res.continuousEndTime;
                    } else {
                      // Middle day: full day 00:00 - 23:59
                      effectiveStartTime = [0, 0];
                      effectiveEndTime = [23, 59];
                    }
                  }

                  const startMinutes = parseTimeToLocalMinutes(
                    res.reservationDate,
                    effectiveStartTime,
                  );
                  const endMinutes = parseTimeToLocalMinutes(
                    res.reservationDate,
                    effectiveEndTime,
                  );
                  const currentMinutes = now.getHours() * 60 + now.getMinutes();

                  return (
                    currentMinutes >= startMinutes &&
                    currentMinutes < endMinutes
                  );
                });

                return (
                  <Box
                    key={asset.id}
                    sx={{
                      bgcolor: themeColors.cardBg,
                      borderRadius: 4,
                      p: 2.5,
                      boxShadow: isDarkMode
                        ? "0 1px 3px rgba(0,0,0,0.2)"
                        : "0 1px 3px rgba(0,0,0,0.08)",
                      border: `1px solid ${isCurrentlyInUse ? "green" : themeColors.border}`,
                      "&:hover": {
                        boxShadow: isDarkMode
                          ? "0 4px 12px rgba(0,0,0,0.3)"
                          : "0 4px 12px rgba(0,0,0,0.1)",
                      },
                      transition: "box-shadow 0.2s",
                      display: "flex",
                      flexDirection: "column",
                      overflow: "hidden",
                      maxWidth: "100%",
                    }}
                  >
                    {/* Card Header - Name and Reserve Button */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        mb: 1,
                      }}
                    >
                      <Box>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 700,
                            color: themeColors.textPrimary,
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            fontSize: "1.1rem",
                          }}
                        >
                          {asset.name}
                          {isCurrentlyInUse && (
                            <Box
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                px: 1,
                                py: 0.25,
                                borderRadius: 1,
                                bgcolor: "green",
                                color: "white",
                                fontSize: "10px",
                                fontWeight: 500,
                                textTransform: "uppercase",
                                letterSpacing: "0.05em",
                              }}
                            >
                              {t("reservations.inUse")}
                            </Box>
                          )}
                        </Typography>
                        {/* Type and Category Tags */}
                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 1,
                            mt: 1,
                          }}
                        >
                          <Box
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              px: 1,
                              py: 0.25,
                              borderRadius: 1,
                              bgcolor: themeColors.tagBg,
                              fontSize: "10px",
                              fontWeight: 500,
                              color: themeColors.textSecondary,
                              textTransform: "uppercase",
                              letterSpacing: "0.05em",
                            }}
                          >
                            {asset.type}
                          </Box>
                          {asset.category && (
                            <Box
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 0.5,
                                px: 1,
                                py: 0.25,
                                borderRadius: 1,
                                bgcolor: themeColors.tagBgCategory,
                                fontSize: "10px",
                                fontWeight: 500,
                                color: themeColors.tagTextCategory,
                                textTransform: "uppercase",
                                letterSpacing: "0.05em",
                              }}
                            >
                              <LocalOfferIcon sx={{ fontSize: 10 }} />
                              {asset.category}
                            </Box>
                          )}
                        </Box>
                      </Box>

                      {/* Admin Actions */}

                      <Box sx={{ display: "flex", gap: 1 }}>
                        {isAdmin && (
                          <>
                            <IconButton
                              size="small"
                              onClick={() => handleEditAsset(asset)}
                              sx={{
                                color: themeColors.textSecondary,
                                "&:hover": {
                                  bgcolor: themeColors.hoverBg,
                                  color: "#3b82f6",
                                },
                              }}
                            >
                              <EditIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteAsset(asset)}
                              sx={{
                                color: themeColors.textSecondary,
                                "&:hover": {
                                  bgcolor: isDarkMode
                                    ? "rgba(239,68,68,0.15)"
                                    : "#fef2f2",
                                  color: "#ef4444",
                                },
                              }}
                            >
                              <DeleteIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </>
                        )}
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                          onClick={() => handleReserveAsset(asset)}
                          color="primary"
                          sx={{
                            borderRadius: 2,
                            fontWeight: 500,
                            fontSize: "0.875rem",
                            textTransform: "none",
                            boxShadow: "none",
                            flexShrink: 0,
                          }}
                        >
                          {t("reservations.reserve")}
                        </Button>
                      </Box>
                    </Box>

                    {/* Description and Expiration Info */}
                    {(asset.description || asset.expirationDate) && (
                      <Box
                        sx={{
                          mb: 1,
                          bgcolor: themeColors.inputBg,
                          p: 1.5,
                          borderRadius: 2,
                          border: `1px solid ${themeColors.borderLight}`,
                        }}
                      >
                        {asset.description && (
                          <Box
                            sx={{
                              display: "flex",
                              gap: 1,
                              alignItems: "flex-start",
                              mb: asset.expirationDate ? 1 : 0,
                            }}
                          >
                            <DescriptionIcon
                              sx={{
                                fontSize: 14,
                                color: themeColors.textMuted,
                                mt: 0.25,
                                flexShrink: 0,
                              }}
                            />
                            <Typography
                              variant="body2"
                              sx={{
                                color: themeColors.textSecondary,
                                fontSize: "0.75rem",
                                lineHeight: 1.4,
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                              }}
                            >
                              {asset.description}
                            </Typography>
                          </Box>
                        )}
                        {asset.expirationDate && (
                          <Box
                            sx={{
                              display: "flex",
                              gap: 1,
                              alignItems: "center",
                            }}
                          >
                            <CalendarTodayIcon
                              sx={{
                                fontSize: 14,
                                color: themeColors.textMuted,
                                flexShrink: 0,
                              }}
                            />
                            <Typography
                              variant="body2"
                              sx={{
                                color: themeColors.textSecondary,
                                fontSize: "0.75rem",
                                fontFamily: "monospace",
                              }}
                            >
                              Expires: {asset.expirationDate}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    )}

                    {/* Reservation List for Selected Date */}
                    <Box
                      sx={{
                        mt: 1,
                        pt: 1,
                        borderTop: `1px solid ${themeColors.borderLight}`,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "10px",
                            fontWeight: 700,
                            color: themeColors.textMuted,
                            textTransform: "uppercase",
                            letterSpacing: "0.05em",
                          }}
                        >
                          {t("reservations.scheduleFor")} {selectedDate}
                        </Typography>
                      </Box>
                      {/* Timeline Section - scrollable container */}
                      <Box
                        sx={{
                          position: "relative",
                          pb: 1,
                          overflow: "hidden",
                          width: "100%",
                        }}
                      >
                        <TimelineBar
                          reservations={assetReservations}
                          date={selectedDate}
                          currentUserId={currentUserId}
                          isAdmin={isAdmin}
                          onDelete={(res) => handleDeleteReservationClick(res)}
                          isDarkMode={isDarkMode}
                        />
                      </Box>
                      <Box sx={{ maxHeight: 160, overflowY: "auto", pr: 0.5 }}>
                        {assetReservations.length === 0 ? (
                          <Typography
                            sx={{
                              fontSize: "0.75rem",
                              color: themeColors.textMuted,
                              fontStyle: "italic",
                              py: 1,
                            }}
                          >
                            No active reservations.
                          </Typography>
                        ) : (
                          assetReservations
                            .sort((a, b) => {
                              // Sort by start time in local timezone
                              const aMinutes = parseTimeToLocalMinutes(
                                a.reservationDate,
                                a.startTime,
                              );
                              const bMinutes = parseTimeToLocalMinutes(
                                b.reservationDate,
                                b.startTime,
                              );
                              return aMinutes - bMinutes;
                            })
                            .map((res) => {
                              const isMine = res.employeeId === currentUserId;
                              const canDelete = isAdmin || isMine;
                              const isRecurring = !!res.recurrenceGroupId;
                              const isContinuous =
                                !!res.continuousGroupId &&
                                res.continuousStartDate &&
                                res.continuousEndDate;

                              // For continuous reservations, calculate effective times for this day
                              let effectiveStartTime: any = res.startTime;
                              let effectiveEndTime: any = res.endTime;

                              if (isContinuous) {
                                const currentDateStr = parseDateToLocalString(
                                  res.reservationDate,
                                  res.startTime,
                                );
                                const contStartDateStr = parseDateToLocalString(
                                  res.continuousStartDate,
                                  res.continuousStartTime,
                                );
                                const contEndDateStr = parseDateToLocalString(
                                  res.continuousEndDate,
                                  res.continuousEndTime,
                                );

                                const isFirstDay =
                                  currentDateStr === contStartDateStr;
                                const isLastDay =
                                  currentDateStr === contEndDateStr;

                                if (isFirstDay && isLastDay) {
                                  effectiveStartTime = res.continuousStartTime;
                                  effectiveEndTime = res.continuousEndTime;
                                } else if (isFirstDay) {
                                  effectiveStartTime = res.continuousStartTime;
                                  effectiveEndTime = [23, 59];
                                } else if (isLastDay) {
                                  effectiveStartTime = [0, 0];
                                  effectiveEndTime = res.continuousEndTime;
                                } else {
                                  effectiveStartTime = [0, 0];
                                  effectiveEndTime = [23, 59];
                                }
                              }

                              const startTimeStr = parseTimeToLocalString(
                                res.reservationDate,
                                effectiveStartTime,
                              );
                              const endTimeStr = parseTimeToLocalString(
                                res.reservationDate,
                                effectiveEndTime,
                              );
                              return (
                                isMine && (
                                  <Box
                                    key={res.id}
                                    sx={{
                                      fontSize: "0.875rem",
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                      p: 1.25,
                                      borderRadius: 2,
                                      mb: 1,
                                      border: "1px solid",
                                      borderColor: themeColors.myBookingBorder,
                                      bgcolor: themeColors.myBookingBg,
                                      color: themeColors.myBookingText,
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 1,
                                        }}
                                      >
                                        <Typography
                                          sx={{
                                            fontWeight: 700,
                                            fontSize: "0.75rem",
                                          }}
                                        >
                                          {startTimeStr || "00:00"} -{" "}
                                          {endTimeStr || "23:59"}
                                          {isContinuous && (
                                            <span
                                              style={{
                                                fontWeight: 400,
                                                marginLeft: 4,
                                              }}
                                            >
                                              ({t("reservations.multiDay")})
                                            </span>
                                          )}
                                        </Typography>
                                        {isRecurring && (
                                          <RepeatIcon
                                            sx={{
                                              fontSize: 10,
                                              color: themeColors.primary,
                                            }}
                                          />
                                        )}
                                      </Box>
                                      <Typography
                                        sx={{ fontSize: "10px", opacity: 0.7 }}
                                      >
                                        {`${res.employeeName || "User"}`}
                                      </Typography>
                                    </Box>
                                    {canDelete && (
                                      <IconButton
                                        size="small"
                                        onClick={() =>
                                          handleDeleteReservationClick(res)
                                        }
                                        sx={{
                                          p: 0.75,
                                          borderRadius: 1,
                                          color: themeColors.primary,
                                          "&:hover": {
                                            bgcolor:
                                              themeColors.myBookingBorder,
                                            color: "#ef4444",
                                          },
                                        }}
                                        title={
                                          isAdmin && !isMine
                                            ? "Admin Force Delete"
                                            : "Cancel Reservation"
                                        }
                                      >
                                        <DeleteIcon sx={{ fontSize: 14 }} />
                                      </IconButton>
                                    )}
                                  </Box>
                                )
                              );
                            })
                        )}
                      </Box>
                    </Box>
                  </Box>
                );
              })
            )}
          </Box>
        )}
      </Container>

      {/* Dialogs */}
      <AssetDialog
        open={assetDialogOpen}
        onClose={() => setAssetDialogOpen(false)}
        onSave={handleSaveAsset}
        asset={selectedAsset}
      />

      <ReservationDialog
        open={reservationDialogOpen}
        onClose={() => setReservationDialogOpen(false)}
        onReserve={handleCreateReservation}
        asset={selectedAsset}
      />

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleConfirmDelete}
        itemName={assetToDelete?.name || ""}
      />

      <DeleteReservationDialog
        open={reservationDeleteDialogOpen}
        onClose={() => setReservationDeleteDialogOpen(false)}
        reservation={reservationToDelete}
        onDeleteSingle={handleDeleteSingleReservation}
        onDeleteRecurring={handleDeleteRecurringReservations}
        onDeleteContinuous={handleDeleteContinuousReservations}
      />

      {/* Success Message Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage(null)}
        message={successMessage}
      />
    </Box>
  );
};

export default ReservationsPage;
